import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from 'sweetalert2';

const RegistrarAsistencia = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [nombreActividad, setNombreActividad] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState('');
  const [matriculaId, setMatriculaId] = useState('');
  const [aprendices, setAprendices] = useState([]);
  const [asistencias, setAsistencias] = useState({});
  const [tieneAsistencias, setTieneAsistencias] = useState(false);
  const [tarjetaUID, setTarjetaUID] = useState('');
  const uidInputRef = useRef(null); // Referencia al input

  useEffect(() => {
    const obtenerActividad = async () => {
      try {
        const res = await axios.get(`/api/actividad/listid/${id}`);
        const datos = res.data;
        setNombreActividad(datos.nombreActividad || '');
        setDescripcion(datos.descripcion || '');
        setFecha(datos.fecha ? datos.fecha.substring(0, 10) : '');
        setMatriculaId(datos.matricula);
      } catch (error) {
        console.error('Error al obtener la actividad:', error);
      }
    };
    obtenerActividad();
  }, [id]);

  useEffect(() => {
    const obtenerAprendices = async () => {
      try {
        if (!matriculaId) return;
        const res = await axios.get(`/api/detallematricula/listbyid/${matriculaId}`);
        const datos = res.data;
        setAprendices(datos.detalles || []);

        const asistenciasInicial = {};
        datos.detalles.forEach((aprendiz) => {
          asistenciasInicial[aprendiz._id] = {
            asistio: false,
            justificacion: ''
          };
        });
        setAsistencias(asistenciasInicial);
      } catch (error) {
        console.error('Error al obtener aprendices:', error);
      }
    };
    obtenerAprendices();
  }, [matriculaId]);

  useEffect(() => {
    const obtenerAsistencias = async () => {
      try {
        const res = await axios.get(`/api/asistencia/actividad/${id}`);
        const asistenciasExistentes = res.data;

        if (asistenciasExistentes && asistenciasExistentes.length > 0) {
          const nuevoEstado = {};
          asistenciasExistentes.forEach((asistencia) => {
            nuevoEstado[asistencia.detalleMatricula] = {
              id: asistencia._id,
              asistio: asistencia.asistio,
              justificacion: asistencia.justificacion
            };
          });

          setAsistencias(prev => ({
            ...prev,
            ...nuevoEstado
          }));

          setTieneAsistencias(true);
        } else {
          setTieneAsistencias(false);
        }
      } catch (error) {
        console.warn("No se pudieron cargar asistencias previas:", error.message);
        setTieneAsistencias(false);
      }
    };

    if (aprendices.length > 0) {
      obtenerAsistencias();
    }
  }, [aprendices, id]);

  const handleCheckboxChange = (idAprendiz) => {
    setAsistencias((prev) => ({
      ...prev,
      [idAprendiz]: {
        ...prev[idAprendiz],
        asistio: !prev[idAprendiz].asistio
      }
    }));
  };

  const handleJustificacionChange = (idAprendiz, value) => {
    setAsistencias((prev) => ({
      ...prev,
      [idAprendiz]: {
        ...prev[idAprendiz],
        justificacion: value
      }
    }));
  };

  const handleUIDScan = async (e) => {
    if (e.key === 'Enter' && tarjetaUID.trim() !== '') {
      e.preventDefault();

      try {
        const res = await axios.post('/api/tarjetas/buscar-detalle-matricula', {
          tarjetaUID: tarjetaUID.trim()
        });

        const { detalleMatriculaId, nombreCompleto } = res.data;

        // Activar asistencia en estado local
        setAsistencias(prev => ({
          ...prev,
          [detalleMatriculaId]: {
            ...(prev[detalleMatriculaId] || {}),
            asistio: true
          }
        }));

        Swal.fire({
          icon: 'success',
          title: '✅ Asistencia registrada',
          html: `<p><strong>${nombreCompleto || 'Aprendiz'}</strong></p>
                  <p class="mb-0">PRESENTE EN LA FORMACIÓN</p>`,
          timer: 2500,
          showConfirmButton: false
        });

        setTarjetaUID(''); // Limpiar el input
        uidInputRef.current?.focus(); // Mantener el foco
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Tarjeta no reconocida',
          text: error.response?.data?.message || 'No se pudo procesar la tarjeta'
        });

        setTarjetaUID('');            // Limpiar el campo si no existe
        uidInputRef.current?.focus(); // Mantener el foco
      }
    }
  };


  const saveAsistencia = async () => {
    try {
      const registros = aprendices.map(aprendiz => {
        const datos = asistencias[aprendiz._id] || {};
        return {
          actividad: id,
          detalleMatricula: aprendiz._id,
          asistio: datos.asistio || false,
          justificacion: datos.justificacion || ''
        };
      });

      await axios.post('/api/asistencia/addmany', registros, {
        headers: { 'Content-Type': 'application/json' }
      });

      Swal.fire({
        icon: 'success',
        title: 'Asistencias registradas',
        text: '✅ Las asistencias se guardaron correctamente.',
        confirmButtonText: 'Aceptar'
      }).then(() => {
        navigate(-1);
      });

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: error.response?.data?.error || error.message || 'Ocurrió un error al registrar las asistencias.'
      });
    }
  };

  const updateAsistencia = async () => {
    try {
      const registros = aprendices.map(aprendiz => {
        const datos = asistencias[aprendiz._id] || {};
        return {
          id: datos.id,
          justificacion: datos.justificacion || ''
        };
      });

      const response = await axios.put('/api/asistencia/updatemany', registros, {
        headers: { 'Content-Type': 'application/json' }
      });

      Swal.fire({
        icon: 'success',
        title: 'Asistencias actualizadas',
        text: `${response.data.actualizados || registros.length} registros actualizados correctamente.`,
        confirmButtonText: 'Aceptar'
      }).then(() => {
        navigate(-1);
      });

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error al actualizar',
        text: error.response?.data?.error || error.message || 'Ocurrió un error al actualizar las asistencias.'
      });
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    tieneAsistencias ? await updateAsistencia() : await saveAsistencia();
  };

  return (
    <div className="container-fluid py-1 px-0 shadow-lg rounded-4 bg-light mt-2 mb-5 ">
      < div className='container-fluid mt-3' >
        <h1>Registro de Asistencias</h1>
      </div>

      <form onSubmit={actions}>

        <div className="container mt-3">

          <div className="row d-flex justify-content-between">

            <div className="col-md-4 mb-5">
              <div className="border p-3 rounded bg-white">
                <div className="mb-3">
                  <label className="form-label">Nombre de la Actividad</label>
                  <input type="text" className="form-control" value={nombreActividad} readOnly />
                </div>

                <div className="mb-3">
                  <label className="form-label">Descripción</label>
                  <textarea className="form-control" value={descripcion} rows={3} readOnly />
                </div>

                <div className="mb-3">
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-control" value={fecha} readOnly />
                </div>

                <div className="mb-3">
                  <label className="form-label">Escanear Tarjeta UID</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Escanee la tarjeta del aprendiz"
                    value={tarjetaUID}
                    onChange={(e) => setTarjetaUID(e.target.value)}
                    onKeyDown={handleUIDScan}
                  />
                </div>

                <div className="d-grid mt-2 mb-3">
                  <button
                    type="submit"
                    className={`btn ${tieneAsistencias ? 'btn-primary' : 'btn-success'} w-100 mt-3`}
                  >
                    {tieneAsistencias ? 'Actualizar' : 'Guardar'}
                  </button>

                  <button type="button" className="btn btn-danger mt-2" onClick={() => navigate(-1)}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-8">
              {/* Vista en Tabla (solo visible en pantallas medianas en adelante) */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-bordered align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Aprendiz</th>
                      <th className="text-center">Asistencia</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aprendices.length > 0 ? (
                      aprendices.map((item, index) => (
                        <tr key={index} className={asistencias[item._id]?.asistio ? 'table-success' : ''}>
                          <td>{item.nombreAprendiz}</td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={asistencias[item._id]?.asistio || false}
                              onChange={() => handleCheckboxChange(item._id)}
                              disabled={tieneAsistencias}
                            />
                          </td>
                          <td>
                            <textarea
                              className="form-control"
                              rows="1"
                              value={asistencias[item._id]?.justificacion || ''}
                              onChange={(e) =>
                                handleJustificacionChange(item._id, e.target.value)
                              }
                            ></textarea>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">No hay aprendices registrados</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Vista en Cards (solo visible en pantallas pequeñas) */}
              <div className="d-md-none">
                {aprendices.length > 0 ? (
                  aprendices.map((item, index) => (
                    <div
                      key={index}
                      className={`card mb-3 ${asistencias[item._id]?.asistio ? 'border-success' : ''}`}
                    >
                      <div className="card-body">
                        <h5 className="card-title">{item.nombreAprendiz}</h5>
                        <div className="mb-2">
                          <label className="form-label">Asistencia</label>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={asistencias[item._id]?.asistio || false}
                              onChange={() => handleCheckboxChange(item._id)}
                              disabled={tieneAsistencias}
                              id={`asistencia-${item._id}`}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`asistencia-${item._id}`}
                            >
                              Asistió
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="form-label">Observaciones</label>
                          <textarea
                            className="form-control"
                            rows="2"
                            value={asistencias[item._id]?.justificacion || ''}
                            onChange={(e) =>
                              handleJustificacionChange(item._id, e.target.value)
                            }
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="alert alert-info text-center">No hay aprendices registrados</div>
                )}
              </div>
            </div>

          </div>

        </div>

      </form>
    </div>
  );
};

export default RegistrarAsistencia;
